/**
 * One shared read of `/api/progress` for the whole hub.
 *
 * Calling, Skills, Marks, Contracts and the header XP bar all need the same document, and
 * five independent `useLoader`s would mean five requests every time the hub opens — and
 * five chances to disagree about how many skill points are left. So the fetch lives here,
 * the tabs subscribe, and a write (unlock a node, pick a Calling) patches the store
 * optimistically and then re-reads.
 */

import { fetchProgress, type ProgressResponse } from './progress-api.js';

export interface ProgressState {
  data: ProgressResponse | null;
  loading: boolean;
  /** True once a fetch has completed, so `data === null` can mean "unreachable". */
  loaded: boolean;
}

type Listener = (s: ProgressState) => void;

class ProgressStore {
  private state: ProgressState = { data: null, loading: false, loaded: false };
  private readonly listeners = new Set<Listener>();
  /** In-flight request, so two tabs mounting at once share one round trip. */
  private inflight: Promise<void> | null = null;
  private lastToken = ' ';

  get current(): ProgressState {
    return this.state;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => {
      this.listeners.delete(fn);
    };
  }

  private set(patch: Partial<ProgressState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of [...this.listeners]) {
      try {
        fn(this.state);
      } catch (err) {
        console.error('[stillsun] progress listener threw', err);
      }
    }
  }

  /** Fetches once per token unless `force`. Never throws. */
  load(token: string, force = false): Promise<void> {
    // Before sign-in there is nothing to read, and asking anyway logged an "unauthorized"
    // warning on the callsign screen every boot. The end state is exactly what the failed
    // request used to produce — answered, with nothing — so an offline or anonymous hub
    // still reads "the ledger is silent" rather than sitting on "reading…" forever.
    if (token.length === 0) {
      this.lastToken = token;
      this.set({ data: null, loading: false, loaded: true });
      return Promise.resolve();
    }
    if (!force && token === this.lastToken && this.state.loaded) return Promise.resolve();
    if (this.inflight && !force) return this.inflight;
    this.lastToken = token;
    this.set({ loading: true });
    const run = fetchProgress(token).then(
      (data) => {
        this.inflight = null;
        this.set({ data, loading: false, loaded: true });
      },
      () => {
        this.inflight = null;
        this.set({ data: null, loading: false, loaded: true });
      },
    );
    this.inflight = run;
    return run;
  }

  /** Optimistic local edit; the caller re-`load`s once the server has answered. */
  patch(change: Partial<ProgressResponse>): void {
    if (!this.state.data) return;
    this.set({ data: { ...this.state.data, ...change } });
  }

  /** Drops the cache so the next `load` really goes out (after a raid, or a respec). */
  invalidate(): void {
    this.set({ loaded: false });
    this.lastToken = ' ';
  }
}

export const progressStore = new ProgressStore();
